import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import Pagination from '@/components/Pagination';
import { usePageTitle } from '@/hooks/usePageTitle';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  History, 
  Search, 
  Filter, 
  User, 
  Calendar,
  UserPlus,
  UserMinus,
  Edit,
  Trash2,
  Eye,
  LogIn,
  CheckCircle,
  ClipboardList,
  Package,
  DollarSign,
  MapPin,
  Trophy,
  Dumbbell,
  Users,
  Loader2
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { useAuth } from '@/context/AuthContext';

const BACKEND_URL = (window.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || "");
const API = `${BACKEND_URL}/api`;

// Icon mapping for action types
const getActionIcon = (actionType) => {
  switch (actionType) {
    case 'create': return <UserPlus className="w-4 h-4" />;
    case 'update': return <Edit className="w-4 h-4" />;
    case 'delete': return <Trash2 className="w-4 h-4" />;
    case 'view': return <Eye className="w-4 h-4" />;
    case 'login': return <LogIn className="w-4 h-4" />;
    case 'validate': return <CheckCircle className="w-4 h-4" />;
    case 'attendance': return <ClipboardList className="w-4 h-4" />;
    default: return <History className="w-4 h-4" />;
  }
};

// Color mapping for action types
const getActionColor = (actionType) => {
  switch (actionType) {
    case 'create': return 'bg-green-100 text-green-700 border-green-300';
    case 'update': return 'bg-blue-100 text-blue-700 border-blue-300';
    case 'delete': return 'bg-red-100 text-red-700 border-red-300';
    case 'view': return 'bg-gray-100 text-gray-700 border-gray-300';
    case 'login': return 'bg-purple-100 text-purple-700 border-purple-300';
    case 'validate': return 'bg-brand-100 text-brand-700 border-brand-300';
    case 'attendance': return 'bg-amber-100 text-amber-700 border-amber-300';
    default: return 'bg-gray-100 text-gray-700 border-gray-300';
  }
};

// Icon mapping for entity types
const getEntityIcon = (entityType) => {
  switch (entityType) {
    case 'player': return <Users className="w-4 h-4" />;
    case 'material': return <Package className="w-4 h-4" />;
    case 'finance': return <DollarSign className="w-4 h-4" />;
    case 'location': return <MapPin className="w-4 h-4" />;
    case 'match': return <Trophy className="w-4 h-4" />;
    case 'training': return <Dumbbell className="w-4 h-4" />;
    case 'user': return <User className="w-4 h-4" />;
    case 'evaluation': return <ClipboardList className="w-4 h-4" />;
    case 'attendance': return <CheckCircle className="w-4 h-4" />;
    default: return <History className="w-4 h-4" />;
  }
};

// Action type labels
const actionTypeLabels = {
  create: 'Creación',
  update: 'Edición',
  delete: 'Eliminación',
  view: 'Vista',
  login: 'Inicio de sesión',
  logout: 'Cierre de sesión',
  validate: 'Validación',
  attendance: 'Asistencia'
};

// Entity type labels
const entityTypeLabels = {
  player: 'Jugador',
  material: 'Material',
  finance: 'Finanza',
  location: 'Locación',
  match: 'Partido',
  training: 'Entrenamiento',
  user: 'Usuario',
  category: 'Categoría',
  evaluation: 'Evaluación',
  attendance: 'Asistencia'
};

const ActivityHistory = () => {
  usePageTitle('Historial');
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  
  // Filters
  const [filterUser, setFilterUser] = useState('all');
  const [filterEntityType, setFilterEntityType] = useState('all');
  const [filterActionType, setFilterActionType] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterUser, filterEntityType, filterActionType, dateFrom, dateTo, debouncedSearch]);

  const handleItemsPerPageChange = (newSize) => {
    setItemsPerPage(newSize);
    setCurrentPage(1);
  };

  // Fetch logs
  useEffect(() => {
    fetchLogs();
  }, [currentPage, filterUser, filterEntityType, filterActionType, dateFrom, dateTo, debouncedSearch, itemsPerPage]);

  // Fetch users for filter (admin only)
  useEffect(() => {
    if (isAdmin) {
      fetchUsers();
    }
  }, [isAdmin]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('page', currentPage);
      params.append('limit', itemsPerPage);
      
      if (filterUser !== 'all') params.append('user_id', filterUser);
      if (filterEntityType !== 'all') params.append('entity_type', filterEntityType);
      if (filterActionType !== 'all') params.append('action_type', filterActionType);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (debouncedSearch) params.append('search', debouncedSearch);
      
      const response = await axios.get(`${API}/activity-logs?${params.toString()}`);
      setLogs(response.data.data || []);
      setTotalPages(response.data.pagination?.total_pages || 1);
      setTotalItems(response.data.pagination?.total || 0);
    } catch (error) {
      console.error('Error fetching logs:', error);
      toast.error('Error al cargar historial');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/activity-logs/users`);
      setUsers(response.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const formatDate = (dateStr) => {
    try {
      const date = parseISO(dateStr);
      return format(date, "dd.MM.yyyy", { locale: es });
    } catch {
      return dateStr;
    }
  };

  const formatTime = (dateStr) => {
    try {
      const date = parseISO(dateStr);
      return format(date, "HH:mm", { locale: es });
    } catch {
      return '';
    }
  };

  const clearFilters = () => {
    setFilterUser('all');
    setFilterEntityType('all');
    setFilterActionType('all');
    setDateFrom('');
    setDateTo('');
    setSearchTerm('');
  };

  return (
    <Layout>
      <div className="space-y-4 md:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-2">
              <History className="w-7 h-7 text-brand-600" />
              Historial de Actividad
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              {totalItems} registro(s) encontrado(s)
            </p>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col gap-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Buscar en descripción..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              
              {/* Filter Row */}
              <div className="flex flex-wrap gap-2">
                {/* User Filter (admin only) */}
                {isAdmin && (
                  <Select value={filterUser} onValueChange={setFilterUser}>
                    <SelectTrigger className="w-[160px]">
                      <User className="w-4 h-4 mr-2 text-gray-400" />
                      <SelectValue placeholder="Usuario" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos los usuarios</SelectItem>
                      {users.map(u => (
                        <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                
                {/* Entity Type Filter */}
                <Select value={filterEntityType} onValueChange={setFilterEntityType}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="Módulo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los módulos</SelectItem>
                    <SelectItem value="player">Jugadores</SelectItem>
                    <SelectItem value="material">Materiales</SelectItem>
                    <SelectItem value="finance">Finanzas</SelectItem>
                    <SelectItem value="training">Entrenamientos</SelectItem>
                    <SelectItem value="match">Partidos</SelectItem>
                    <SelectItem value="location">Locaciones</SelectItem>
                    <SelectItem value="evaluation">Evaluaciones</SelectItem>
                    <SelectItem value="attendance">Asistencias</SelectItem>
                  </SelectContent>
                </Select>
                
                {/* Action Type Filter */}
                <Select value={filterActionType} onValueChange={setFilterActionType}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="Acción" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas las acciones</SelectItem>
                    <SelectItem value="create">Creación</SelectItem>
                    <SelectItem value="update">Edición</SelectItem>
                    <SelectItem value="delete">Eliminación</SelectItem>
                    <SelectItem value="validate">Validación</SelectItem>
                    <SelectItem value="attendance">Asistencia</SelectItem>
                  </SelectContent>
                </Select>
                
                {/* Date From */}
                <div className="flex items-center gap-1">
                  <span className="text-sm text-gray-500 hidden sm:inline">Desde:</span>
                  <Input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="w-[140px]"
                  />
                </div>
                
                {/* Date To */}
                <div className="flex items-center gap-1">
                  <span className="text-sm text-gray-500 hidden sm:inline">Hasta:</span>
                  <Input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="w-[140px]"
                  />
                </div>
                
                {/* Clear Filters */}
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  <Filter className="w-4 h-4 mr-1" />
                  Limpiar
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Activity Timeline */}
        <Card>
          <CardContent className="p-4 md:p-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-brand-600" />
              </div>
            ) : logs.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <History className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No hay registros de actividad</p>
                <p className="text-sm">Las acciones realizadas aparecerán aquí</p>
              </div>
            ) : (
              <div className="relative">
                {/* Timeline line */}
                <div className="absolute left-4 md:left-6 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                
                {/* Timeline items */}
                <div className="space-y-4">
                  {logs.map((log, index) => (
                    <div key={log.id} className="relative flex gap-4 md:gap-6">
                      {/* Timeline dot */}
                      <div className={`relative z-10 flex-shrink-0 w-8 h-8 md:w-12 md:h-12 rounded-full flex items-center justify-center border-2 ${getActionColor(log.action_type)}`}>
                        {getActionIcon(log.action_type)}
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 bg-white border rounded-lg p-3 md:p-4 shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                          <div className="flex-1">
                            {/* Description */}
                            <p className="text-sm md:text-base text-gray-900 font-medium">
                              {log.description}
                            </p>
                            
                            {/* Meta info */}
                            <div className="flex flex-wrap items-center gap-2 mt-2">
                              {/* User */}
                              <Badge variant="outline" className="text-xs">
                                <User className="w-3 h-3 mr-1" />
                                {log.user_name}
                                {log.user_role && log.user_role !== 'admin' && (
                                  <span className="ml-1 opacity-60">({log.user_role})</span>
                                )}
                              </Badge>
                              
                              {/* Entity type */}
                              <Badge variant="outline" className="text-xs">
                                {getEntityIcon(log.entity_type)}
                                <span className="ml-1">{entityTypeLabels[log.entity_type] || log.entity_type}</span>
                              </Badge>
                              
                              {/* Action type */}
                              <Badge className={`text-xs ${getActionColor(log.action_type)}`}>
                                {actionTypeLabels[log.action_type] || log.action_type}
                              </Badge>
                            </div>
                          </div>
                          
                          {/* Timestamp */}
                          <div className="text-xs text-gray-500 text-right whitespace-nowrap">
                            <div className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {formatDate(log.created_at)}
                            </div>
                            <div className="mt-0.5">
                              {formatTime(log.created_at)} hs
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Pagination */}
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={handleItemsPerPageChange}
              itemName="registros"
            />
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default ActivityHistory;
